<script>
  import { createEventDispatcher } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';
  import { getCurrencySymbol } from '../utils';
  import { selectedCountry } from '../countryStore';

  export let showModal = false;
  export let achievementId = null;
  export let promotionName = '';

  const dispatch = createEventDispatcher();
  
  let amount = '';
  let receiptDate = ''; 
  let receiptImageBase64 = '';
  let selectedFileName = '';
  let isLoading = false;
  let error = '';

  $: currency = getCurrencySymbol($selectedCountry);

  // Reactive initialization: Whenever the modal opens, reset the form to defaults
  $: if (showModal) {
      resetForm();
  }

  function resetForm() {
      amount = '';
      // Correctly format today's date as YYYY-MM-DD for the date input
      receiptDate = new Date().toISOString().split('T')[0];
      receiptImageBase64 = '';
      selectedFileName = '';
      error = '';
  }

  function handleFileChange(e) {
      const file = e.target.files[0];
      if (!file) {
          receiptImageBase64 = '';
          selectedFileName = '';
          return;
      }
      
      selectedFileName = file.name;
      
      const reader = new FileReader();
      reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
              const canvas = document.createElement('canvas');
              const MAX_WIDTH = 1000; 
              let scaleSize = 1;
              
              if (img.width > MAX_WIDTH) {
                  scaleSize = MAX_WIDTH / img.width;
              }
              
              canvas.width = img.width * scaleSize;
              canvas.height = img.height * scaleSize;
              
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
              receiptImageBase64 = dataUrl.split(',')[1];
          };
          img.src = event.target.result;
      };
      reader.readAsDataURL(file);
  }

  async function submitTransaction() {
      if (!amount || parseFloat(amount) <= 0) {
          error = $t('log_modal.error_amount') || 'Please enter a valid amount.';
          return;
      }
      
      if (achievementId === true || typeof achievementId === 'boolean' || !achievementId) {
          error = "Achievement reference missing. Please try refreshing or re-joining the challenge.";
          return;
      }

      isLoading = true;
      error = '';

      try {
          const dateObj = new Date(receiptDate);
          
          const payload = {
              achievementId: String(achievementId),
              amount: parseFloat(amount),
              transactionDateTime: dateObj.toISOString(),
              receiptImageBase64: receiptImageBase64
          };

          const response = await authFetch(getApiUrl('/achievements/transactions'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          let result;
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
              result = await response.json();
          } else {
              const errorText = await response.text();
              throw new Error(`Server Error: ${response.status} - ${errorText}`);
          }

          if (!response.ok) throw new Error(result.error || $t('log_modal.error_failed'));

          dispatch('success');
          closeModal();
      } catch (e) {
          error = e.message;
      } finally {
          isLoading = false;
      }
  }

  function closeModal() {
      showModal = false;
      dispatch('close');
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h2>{$t('log_modal.title')}</h2>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>

      <div class="modal-body">
        {#if error}
            <div class="error-banner">{error}</div>
        {/if}

        <div class="form-group">
            <label>{$t('log_modal.promo')}</label>
            <div class="readonly-field">{promotionName}</div>
        </div>

        <div class="form-group">
            <label>{$t('log_modal.date')}</label>
            <input type="date" bind:value={receiptDate} max={new Date().toISOString().split('T')} />
        </div>

        <div class="form-group">
            <label>{$t('log_modal.amount')}</label>
            <div class="amount-input">
                <span class="currency">{currency}</span>
                <input type="number" step="0.01" min="0.01" bind:value={amount} placeholder="0.00" />
            </div>
        </div>

        <div class="form-group">
            <label>{$t('log_modal.receipt')}</label>
            <div class="file-upload">
                <label for="receipt-upload" class="upload-btn">
                    📁 {$t('log_modal.choose_file')}
                </label>
                <input type="file" id="receipt-upload" accept="image/*" on:change={handleFileChange} hidden />
                <span class="file-name">{selectedFileName || 'No file chosen'}</span>
            </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" on:click={closeModal} disabled={isLoading}>{$t('log_modal.cancel')}</button>
        <button class="btn-submit" on:click={submitTransaction} disabled={isLoading || !amount}>
            {isLoading ? $t('log_modal.uploading') : $t('log_modal.submit')}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.6); z-index: 2000;
    display: flex; justify-content: center; align-items: center;
    padding: 1rem; box-sizing: border-box;
  }
  .modal-content {
    background: #fff; width: 100%; max-width: 450px;
    border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    display: flex; flex-direction: column; overflow: hidden;
  }
  .modal-header {
    display: flex; justify-content: space-between; align-items: center;
    background: #f8f9fa; padding: 1.2rem 1.5rem; border-bottom: 1px solid #eee;
  }
  .modal-header h2 { margin: 0; font-size: 1.25rem; color: #333; }
  .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #666; }
  
  .modal-body { padding: 1.5rem; overflow-y: auto; display: flex; flex-direction: column; gap: 1.2rem; }
  
  .form-group { display: flex; flex-direction: column; gap: 0.4rem; text-align: left; }
  .form-group label { font-weight: bold; font-size: 0.9rem; color: #444; }
  
  .readonly-field { padding: 0.8rem; background: #f1f3f5; border-radius: 6px; color: #555; font-size: 0.95rem; word-break: break-word;}
  
  input[type="date"], input[type="number"] {
      padding: 0.8rem; border: 1px solid #ccc; border-radius: 6px; font-size: 1rem; box-sizing: border-box; width: 100%;
      font-family: inherit;
  }
  
  .amount-input { display: flex; align-items: center; position: relative; }
  .amount-input .currency { position: absolute; left: 1rem; color: #666; font-weight: bold; }
  .amount-input input { padding-left: 2.2rem; }
  
  .file-upload { display: flex; align-items: center; gap: 1rem; overflow: hidden;}
  .upload-btn { background: #e2e8f0; border: 1px solid #cbd5e1; padding: 0.6rem 1rem; border-radius: 6px; cursor: pointer; font-size: 0.9rem; color: #334155; transition: background 0.2s; white-space: nowrap; }
  .upload-btn:hover { background: #cbd5e1; }
  .file-name { font-size: 0.85rem; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
  
  .error-banner { background: #fee2e2; color: #b91c1c; padding: 0.8rem; border-radius: 6px; font-size: 0.9rem; border: 1px solid #f87171; }

  .modal-footer { padding: 1.2rem 1.5rem; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 1rem; background: #fafafa; }
  .modal-footer button { padding: 0.7rem 1.5rem; border-radius: 6px; font-weight: bold; font-size: 0.95rem; cursor: pointer; border: none; }
  .btn-cancel { background: #e2e8f0; color: #475569; }
  .btn-submit { background: #007bff; color: white; }
  .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

  @media (max-width: 500px) {
      .modal-content { border-radius: 0; height: 100%; max-height: 100vh; }
      .modal-footer { margin-top: auto; padding-bottom: 2rem;}
  }
</style>